import { useCallback, useRef } from "react";
import { mutate } from "swr";
import type { SWRResponse } from "swr";

import { useAuth } from "@/contexts/auth-context";
import { useApiSWR } from "@/hooks/use-api-client";
import { getApiV1Companies, patchApiV1UserDefaultCompany } from "@/lib/api/sdk-facade.gen";
import { getCompaniesKey } from "@/lib/context-cache-keys";
import type { CompaniesResponse, Company, MeResponse } from "@/lib/generated/types.gen";

const AUTH_ME_SWR_KEY = "auth-me";

export function useCompanyApi(): {
  useCompanies: (enabled?: boolean) => SWRResponse<Company[] | undefined, Error>;
  getCompaniesKey: () => string | null;
  updateDefaultCompany: (
    companyId: number,
    options?: { initiatingUserId?: string | null },
  ) => Promise<void>;
} {
  const { userId } = useAuth();
  const companiesKey = userId != null ? getCompaniesKey(userId) : null;
  const currentUserIdRef = useRef<string | null>(userId);
  currentUserIdRef.current = userId;

  const useCompanies = (enabled: boolean = true): SWRResponse<Company[] | undefined, Error> =>
    useApiSWR<Company[] | undefined>(
      enabled === true ? companiesKey : null,
      enabled === true && companiesKey != null
        ? async () => {
            const response: CompaniesResponse = await getApiV1Companies();
            return Array.isArray(response.items) ? response.items : [];
          }
        : undefined,
    );

  const updateDefaultCompany = useCallback(
    async (companyId: number, options?: { initiatingUserId?: string | null }): Promise<void> => {
      await patchApiV1UserDefaultCompany({
        body: {
          company_id: companyId,
        },
      });

      if (
        options?.initiatingUserId != null &&
        currentUserIdRef.current !== options.initiatingUserId
      ) {
        return;
      }

      await mutate(
        AUTH_ME_SWR_KEY,
        (current: MeResponse | undefined) => {
          if (current == null) {
            return current;
          }

          return {
            ...current,
            default_company_id: companyId,
          };
        },
        false,
      );
    },
    [],
  );

  return {
    useCompanies,
    getCompaniesKey: () => companiesKey,
    updateDefaultCompany,
  };
}
